import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

export const REFERENCE_SOURCE_OPTIONS = [
  { value: "banner", label: "Banner Advertisement" },
  { value: "friend", label: "Through a Friend" },
  { value: "reference", label: "Someone's Reference" },
  { value: "other", label: "Other" },
] as const;

export type ReferenceSource =
  (typeof REFERENCE_SOURCE_OPTIONS)[number]["value"];

export interface VisitorFamilyMember {
  id?: number;
  sr_no: number;
  name: string;
  relationship: number;
  relationship_name?: string;
  date_of_birth?: string;
  education?: string;
}

export interface VisitorFamilyMemberPayload {
  sr_no: number;
  name: string;
  relationship: number;
  date_of_birth?: string;
  education?: string;
}

export interface VisitorPayload extends Record<string, unknown> {
  date: string;
  name: string;
  address?: string;
  residence_phone?: string;
  mobile_number?: string;
  business_address?: string;
  email?: string;
  occupation?: string;
  age?: number;
  reason_for_visit?: string;
  has_visited_before: boolean;
  previous_visit_details?: string;
  reference_source?: string;
  referrer_name?: string;
  referrer_mobile?: string;
  total_family_members?: number;
  male_count?: number;
  female_count?: number;
  feedback?: string;
  family_members: VisitorFamilyMemberPayload[];
}

export interface VisitorRecord extends Omit<VisitorPayload, "family_members"> {
  id: number;
  family_members?: VisitorFamilyMember[];
  created_at?: string;
  updated_at?: string;
}

export type VisitorFamilyMemberFormRow = {
  rowId: string;
  sr_no: number;
  name: string;
  relationship: string;
  relationshipName: string;
  date_of_birth: string;
  education: string;
};

export type VisitorFormState = Omit<VisitorRecord, "family_members"> & {
  family_members: VisitorFamilyMemberFormRow[];
  mobile_country?: PhoneCountryCode;
  referrer_mobile_country?: PhoneCountryCode;
};

function trimmed(value: unknown): string | undefined {
  const v = String(value ?? "").trim();
  return v ? v : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === "" || value === undefined || value === null) {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function newFamilyMemberRow(srNo = 1): VisitorFamilyMemberFormRow {
  return {
    rowId: crypto.randomUUID(),
    sr_no: srNo,
    name: "",
    relationship: "",
    relationshipName: "",
    date_of_birth: "",
    education: "",
  };
}

export function mapVisitorToForm(record: VisitorRecord): VisitorFormState {
  const members = record.family_members ?? [];
  return {
    ...record,
    age: record.age ?? undefined,
    has_visited_before: Boolean(record.has_visited_before),
    family_members:
      members.length > 0
        ? members.map(m => ({
            rowId: crypto.randomUUID(),
            sr_no: m.sr_no,
            name: m.name ?? "",
            relationship:
              m.relationship !== null && m.relationship !== undefined
                ? String(m.relationship)
                : "",
            relationshipName: m.relationship_name ?? "",
            date_of_birth: m.date_of_birth ?? "",
            education: m.education ?? "",
          }))
        : [],
  };
}

export type VisitorValidationKey =
  | "dateRequired"
  | "nameRequired"
  | "mobileInvalid"
  | "residencePhoneInvalid"
  | "emailInvalid"
  | "ageInvalid"
  | "referrerMobileInvalid"
  | "familyNameRequired"
  | "familyRelationshipRequired";

export const VISITOR_VALIDATION_MESSAGES: Record<VisitorValidationKey, string> =
  {
    dateRequired: "Please select the visit date.",
    nameRequired: "Please enter the visitor name.",
    mobileInvalid: "Enter a valid mobile number for the selected country.",
    residencePhoneInvalid: "Enter a valid residence phone number.",
    emailInvalid: "Enter a valid email address.",
    ageInvalid: "Enter a valid age between 1 and 120.",
    referrerMobileInvalid:
      "Enter a valid referrer mobile for the selected country.",
    familyNameRequired: "Each family member with a relationship needs a name.",
    familyRelationshipRequired:
      "Each family member with a name needs a relationship.",
  };

const PHONE_RE = /^\d{6,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digitsOnly(value: unknown): string {
  return String(value ?? "").replace(/\D/g, "");
}

export function validateVisitorForm(
  payload: Record<string, unknown>
): VisitorValidationKey | null {
  if (!String(payload.date ?? "").trim()) {
    return "dateRequired";
  }
  if (!String(payload.name ?? "").trim()) {
    return "nameRequired";
  }

  const mobileCountry =
    (payload.mobile_country as PhoneCountryCode | undefined) ??
    DEFAULT_PHONE_COUNTRY;
  if (
    String(payload.mobile_number ?? "").trim() &&
    !isValidMobileNumber(String(payload.mobile_number), mobileCountry, {
      required: false,
    })
  ) {
    return "mobileInvalid";
  }

  const residencePhone = digitsOnly(payload.residence_phone);
  if (
    String(payload.residence_phone ?? "").trim() &&
    !PHONE_RE.test(residencePhone)
  ) {
    return "residencePhoneInvalid";
  }

  const email = String(payload.email ?? "").trim();
  if (email && !EMAIL_RE.test(email)) {
    return "emailInvalid";
  }

  if (payload.age !== undefined && payload.age !== null && payload.age !== "") {
    const age = Number(payload.age);
    if (!Number.isFinite(age) || age < 1 || age > 120) {
      return "ageInvalid";
    }
  }

  const referrerCountry =
    (payload.referrer_mobile_country as PhoneCountryCode | undefined) ??
    DEFAULT_PHONE_COUNTRY;
  if (
    String(payload.referrer_mobile ?? "").trim() &&
    !isValidMobileNumber(String(payload.referrer_mobile), referrerCountry, {
      required: false,
    })
  ) {
    return "referrerMobileInvalid";
  }

  const familyRows = Array.isArray(payload.family_members)
    ? (payload.family_members as VisitorFamilyMemberFormRow[])
    : [];

  for (const row of familyRows) {
    const hasName = Boolean(row.name?.trim());
    const hasRelationship = Boolean(String(row.relationship ?? "").trim());
    if (hasName && !hasRelationship) {
      return "familyRelationshipRequired";
    }
    if (hasRelationship && !hasName) {
      return "familyNameRequired";
    }
  }

  return null;
}

export function buildVisitorPayload(
  payload: Record<string, unknown>
): VisitorPayload {
  const validationError = validateVisitorForm(payload);
  if (validationError) {
    throw new Error(VISITOR_VALIDATION_MESSAGES[validationError]);
  }

  const name = String(payload.name ?? "").trim();
  const date = String(payload.date ?? "").trim();

  const familyRows = Array.isArray(payload.family_members)
    ? (payload.family_members as VisitorFamilyMemberFormRow[])
    : [];

  const family_members = familyRows
    .filter(row => row.name.trim())
    .map((row, index) => ({
      sr_no: index + 1,
      name: row.name.trim(),
      relationship: Number(row.relationship),
      date_of_birth: trimmed(row.date_of_birth),
      education: trimmed(row.education),
    }))
    .filter(row => Number.isFinite(row.relationship));

  const hasVisitedBefore =
    payload.has_visited_before === true ||
    payload.has_visited_before === "true";

  return {
    date,
    name,
    address: trimmed(payload.address),
    residence_phone: trimmed(payload.residence_phone),
    mobile_number: trimmed(payload.mobile_number),
    business_address: trimmed(payload.business_address),
    email: trimmed(payload.email),
    occupation: trimmed(payload.occupation),
    age: optionalNumber(payload.age),
    reason_for_visit: trimmed(payload.reason_for_visit),
    has_visited_before: hasVisitedBefore,
    previous_visit_details: hasVisitedBefore
      ? (trimmed(payload.previous_visit_details) ?? "")
      : "",
    reference_source: trimmed(payload.reference_source),
    referrer_name: trimmed(payload.referrer_name),
    referrer_mobile: trimmed(payload.referrer_mobile),
    total_family_members: optionalNumber(payload.total_family_members),
    male_count: optionalNumber(payload.male_count),
    female_count: optionalNumber(payload.female_count),
    feedback: trimmed(payload.feedback),
    family_members,
  };
}

export function visitorDefaultValues(): Partial<VisitorFormState> {
  return {
    date: new Date().toISOString().slice(0, 10),
    has_visited_before: false,
    previous_visit_details: "",
    total_family_members: 0,
    male_count: 0,
    female_count: 0,
    family_members: [],
    mobile_country: DEFAULT_PHONE_COUNTRY,
    referrer_mobile_country: DEFAULT_PHONE_COUNTRY,
  };
}
